import React, { useState, useEffect, useRef } from "react";
import AddButton from "../syllabi/show/AddButton";
import EditButton from "../syllabi/show/EditButton";
import RemoveButton from "../syllabi/show/RemoveButton";
import SequenceableControls from "../syllabi/show/SequenceableControls";
import DrEditor from "../syllabi/show/Editor";
import Spinner from "../Spinner";
import debounce from "../../utils/debounce";
import reqOptions from "../../utils/req_options";
//apiCall(url, reqOptions, component)
//returns a promise
import apiCall from "../../utils/api_call";
import { display } from "@material-ui/system";

const api = process.env.API_URL;

/**
 * <DropdownSettings />
 */
const DropdownSettings = (props) => {
  /**
   * IMPORTANT: Do not try to log _isMounted.current with
   * console.log("string label", _isMounted.current).
   * This will result in a JS error that crashes the page:
   * "TypeError: Cannot read properties of undefined (reading 'current')"
  **/
  const _isMounted = useRef(false);
  const [dataReady, setDataReady] = useState(false);
  const [tableTypes, setTableTypes] = useState([]);
  const [editType, setEditType] = useState({});
  const [options, setOptions] = useState(null);
  const tableTypesByVal = hashByVal(tableTypes);

  const loadTableTypes = () => {
    apiCall(`${api}lookup_tables`, reqOptions("GET"), {
      _isMounted: _isMounted.current,
    })
      .then((data) => {
        if (_isMounted.current) {
          console.log(data);
          setTableTypes(data);
          setEditType(data[0] || {});
          setDataReady(true);
          console.log(tableTypesByVal);
        }
      })
      .catch((err) => console.error(err));
  };

  const postOption = () => {
    const body = {
      lookup_table: {
        table_type: editType.value,
        sequence: 1,
      },
    };
    apiCall(`${api}lookup_tables`, reqOptions("POST", body), {
      _isMounted: _isMounted.current,
    })
      .then((data) => {
        if (_isMounted.current) setOptions(data);
      })
      .catch((err) => console.error(err));
  };

  const moveOption = (opts, e) => {
    const { id, value } = opts;
    const body = { lookup_table: { sequence: value } };
    apiCall(`${api}lookup_tables/${id}`, reqOptions("PATCH", body), {
      _isMounted: _isMounted.current,
    })
      .then((data) => {
        if (_isMounted.current) setOptions(data);
      })
      .catch((err) => console.error(err));
  };

  const changeEditType = (event) => {
    if (_isMounted.current) {
      setEditType(tableTypesByVal[event.target.value] || {});
    }
  };

  const removeOption = (id) => {
    if (confirm("Are you sure you want to remove this option?")) {
      const body = { lookup_table: { active: false } };
      apiCall(`${api}lookup_tables/${id}`, reqOptions("PATCH", body), {
        _isMounted: _isMounted.current,
      })
        .then((data) => {
          if (_isMounted.current) setOptions(data);
        })
        .catch((err) => console.error(err));
    }
  };

  useEffect(function setMounted() {
    _isMounted.current = true;
    loadTableTypes();
    return function setUnmounted() {
      _isMounted.current = false;
    };
  }, []);

  return dataReady ? (
    <>
      <h1 className="d-flex flex-row justify-content-center">Manage Options</h1>
      <h2 className="d-flex flex-row justify-content-center">
        {editType.name}
      </h2>
      <div className="container card">
        <div className="md-font card border-primary bg-light">
          <div className="card-header text-primary">
            <label htmlFor="tableTypeSelect pr-2">
              <strong>Option Set to Manage:</strong>
              <span className="ml-2">{editType.name}</span>
            </label>
          </div>
          <div className="card-body text-primary d-flex justify-content-between">
            <select
              id="tableTypeSelect"
              className="btn btn-light text-primary border-primary  ml-2 p-2 md-font"
              onChange={(e) => changeEditType(e)}
            >
              {tableTypes.map((type) => (
                <option key={`type-${type.id}`} value={type.value}>
                  {type.name}
                </option>
              ))}
            </select>
            {editType.value ? <AddButton addItem={postOption} /> : ""}
          </div>
          <OptionList
            tableType={editType}
            options={options}
            removeOption={removeOption}
            moveOption={moveOption}
          />
        </div>
      </div>
    </>
  ) : (
    <Spinner />
  );
};

const OptionList = (props) => {
  const _isMounted = useRef(false);
  const { tableType } = props;
  const [dataReady, setDataReady] = useState(false);
  const [options, setOptions] = useState(props.options || []);
  const maxSequence = options.length;

  const loadOptions = () => {
    apiCall(
      `${api}lookup_tables?lookup_table[table_type]=${tableType.value}`,
      reqOptions("GET"),
      { _isMounted: _isMounted.current }
    )
      .then((data) => {
        if (_isMounted.current) {
          console.log(data);
          setOptions(data);
          setDataReady(true);
        }
      })
      .catch((err) => console.error(err));
  };

  useEffect(
    function optionsPropUpdated() {
      loadOptions();
    },
    [props.options]
  );

  useEffect(
    function typePropChanged() {
      if (_isMounted.current) {
        setDataReady(false);
        loadOptions();
      }
    },
    [tableType]
  );

  useEffect(function setMounted() {
    _isMounted.current = true;
    loadOptions();
    return function setUnmounted() {
      _isMounted.current = false;
    };
  }, []);

  return (
    <ul className="list-group">
      {options.map((opt) => (
        <Option
          key={`opt-id-${opt.id}`}
          {...props}
          option={opt}
          maxSequence={maxSequence}
        />
      ))}
    </ul>
  );
};

const Option = (props) => {
  const _isMounted = useRef(false);
  const { moveOption, option, removeOption, tableType } = props;
  const [name, setName] = useState(option.name || "");
  const [value, setValue] = useState(option.value || "");
  const [description, setDescription] = useState(option.description || "");
  const [timer, setTimer] = useState(null);
  const [edit, setEdit] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const toggleEdit = () => {
    if (_isMounted.current) {
      setEdit(!edit);
    }
  };

  const changeName = (event) => {
    if (_isMounted.current) {
      setName(event.target.value);
    }
  };

  const changeValue = (event) => {
    if (_isMounted.current) {
      setValue(event.target.value);
    }
  };

  const changeDescription = (opts, event) => {
    if (_isMounted.current) {
      setDescription(event.target.value);
    }
  };

  const sendUpdate = () => {
    const body = {
      lookup_table: {
        name: name,
        description: description.length > 0 ? description : null,
        value: value.length > 0 ? value : null,
      },
    };
    if (_isMounted.current) {
      setSyncing(true);
      apiCall(`${api}lookup_tables/${option.id}`, reqOptions("PATCH", body), {
        _isMounted: _isMounted.current,
      })
        .then((data) => {
          if (_isMounted.current) setSyncing(false);
        })
        .catch((err) => console.error(err));
    }
  };

  useEffect(
    function sendUpdateEffect() {
      if (_isMounted.current) {
        setTimer(debounce(sendUpdate, _isMounted, timer, 3));
      }
    },
    [name, description, value]
  );

  useEffect(function setMounted() {
    _isMounted.current = true;

    return function setUnmounted() {
      _isMounted.current = false;
    };
  }, []);

  const nameElem = edit ? (
    <input
      id={`opt-${option.id}-name`}
      maxLength="250"
      type="text"
      value={name}
      placeholder="option name or title"
      onChange={changeName}
    />
  ) : (
    <h2 id={`opt-${option.id}-name`} className="text-dark mb-1">
      <span>{name}</span>
    </h2>
  );

  let descElem;
  if (tableType.wysiwyg) {
    descElem = (
      <DrEditor
        text={description}
        handleChange={changeDescription}
        dataReady={true}
        focusKey={edit ? `desc-${option.id}` : ""}
        parentKey={`desc-${option.id}`}
      />
    );
  } else if (edit) {
    descElem = (
      <textarea
        id={`desc-${option.id}input`}
        className="m1-2 p-1 w-75"
        value={description}
        placeholder="option description (optional)"
        onChange={(e) => changeDescription(null, e)}
      />
    );
  } else {
    descElem = (
      <div id={`desc-${option.id}input`} className="text-dark">
        {description}
      </div>
    );
  }

  const valueElem = edit ? (
    <input
      id={`opt-${option.id}-val`}
      maxLength="250"
      type="text"
      value={value}
      placeholder="option value"
      onChange={changeValue}
    />
  ) : (
    <h2 id={`opt-${option.id}-val`} className="text-dark mb-1">
      <span>{value}</span>
    </h2>
  );

  const syncElem = syncing ? (
    <div className="spinner-border text-primary mr-3" role="status">
      <span className="sr-only">Updating...</span>
    </div>
  ) : (
    ""
  );

  return (
    <li className="list-group-item">
      <div className="d-flex justify-content-between">
        <div>
          <label htmlFor={`opt-${option.id}-name`} className="w-100">
            <strong>Name/Title of Option:</strong>
          </label>
          {nameElem}
        </div>
        <div>
          <SequenceableControls
            {...props}
            focus={toggleEdit}
            removeItem={() => removeOption(option.id)}
            handleChange={moveOption}
            item={option}
            editMode={true}
          />
        </div>
      </div>
      <div className="mt-2 mb-2">
        <label htmlFor={`desc-${option.id}input`} className="w-100">
          <strong>Description of Option:</strong>
        </label>
        {descElem}
      </div>
      <div className="mt-2 mb-2 d-flex justify-content-between">
        <div>
          <label htmlFor={`opt-${option.id}-val`} className="w-100">
            <strong>Value of Option:</strong>
          </label>
          {valueElem}
        </div>
        {syncElem}
      </div>
    </li>
  );
};

const hashByVal = (arr) => arr.reduce((h, t) => (h[t.value] = t) && h, {});

export default DropdownSettings;
